import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import { useUsers } from '@/services/users';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';
import User from '@/models/user';
import { UserFilters } from '@/services/users';
import { ORDER_STATUS_OPTIONS, orderStatusLabel } from '@/utils/order-status';
import { getUserDisplayName } from '@/utils/user-display';

const CAMPAIGN_SOURCES = ['dontwarinside', 'bundle', 'telegram_bot'] as const;

const getSorting = (order: 'asc' | 'desc', orderBy: string) => {
    if (order === 'desc') {
        return (a: any, b: any) => {
            if (a[orderBy] < b[orderBy]) {
                return -1;
            }
            if (a[orderBy] > b[orderBy]) {
                return 1;
            }
            return 0;
        };
    }
    return (a: any, b: any) => {
        if (a[orderBy] > b[orderBy]) {
            return -1;
        }
        if (a[orderBy] < b[orderBy]) {
            return 1;
        }
        return 0;
    };
};

const Users = () => {
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState('id');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [campaignFilter, setCampaignFilter] = useState('');
    const [cohortFilter, setCohortFilter] = useState('');
    const [shipmentFilter, setShipmentFilter] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const navigate = useNavigate();

    // Memoised so the object identity is stable — it is part of the query key,
    // and a fresh object every render would refetch on every render.
    const filters: UserFilters = useMemo(
        () => ({
            search: debouncedSearch,
            signupSource: campaignFilter,
            cohort: cohortFilter,
            shipmentStatus: shipmentFilter,
            createdFrom,
            createdTo,
        }),
        [debouncedSearch, campaignFilter, cohortFilter, shipmentFilter, createdFrom, createdTo],
    );

    const { data: usersResponse, isLoading } = useUsers(page + 1, rowsPerPage, filters);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 1000);
        return () => clearTimeout(handler);
    }, [search]);

    const resetToFirstPage = () => setPage(0);

    const filteredUsers: User[] = usersResponse?.data?.users || [];

    const handleRequestSort = (_: React.MouseEvent<unknown>, property: string) => {
        const orderByTemp = property;
        let orderTemp: 'asc' | 'desc' = 'desc';
        if (orderBy === property && order === 'desc') {
            orderTemp = 'asc';
        }

        setOrder(orderTemp);
        setOrderBy(orderByTemp);
    };

    // Row click opens the full profile page (spec v2 §01). The old five-field
    // modal is gone: two edit surfaces for one record is how they drift.
    const handleClick = (id: string) => {
        navigate(`/users/${id}`);
    };

    const handleChangePage = (_: unknown, item: number) => {
        setPage(item);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Users</h3>
                    <h3 className="page-subhead subhead">You can manage the users here</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
                                        <MaterialTextField
                                            style={{ minWidth: '260px' }}
                                            label="Search by Email"
                                            type="text"
                                            value={search}
                                            onChange={(e) => {
                                                setSearch(e.target.value);
                                                resetToFirstPage();
                                            }}
                                        />
                                        <select
                                            className="form-select"
                                            style={{ width: '170px', height: '40px' }}
                                            value={campaignFilter}
                                            onChange={(e) => {
                                                setCampaignFilter(e.target.value);
                                                resetToFirstPage();
                                            }}
                                        >
                                            <option value="">All Sources</option>
                                            {CAMPAIGN_SOURCES.map((src) => (
                                                <option key={src} value={src}>
                                                    {src}
                                                </option>
                                            ))}
                                        </select>
                                        <MaterialTextField
                                            style={{ width: '130px' }}
                                            label="Cohort"
                                            type="text"
                                            value={cohortFilter}
                                            onChange={(e) => {
                                                setCohortFilter(e.target.value);
                                                resetToFirstPage();
                                            }}
                                        />
                                        <select
                                            className="form-select"
                                            style={{ width: '170px', height: '40px' }}
                                            value={shipmentFilter}
                                            onChange={(e) => {
                                                setShipmentFilter(e.target.value);
                                                resetToFirstPage();
                                            }}
                                        >
                                            <option value="">All Shipments</option>
                                            {/* value = the STORED enum, label = the requested wording. */}
                                            {ORDER_STATUS_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="d-flex align-items-center gap-2">
                                            <label className="mb-0 text-nowrap" htmlFor="createdFrom">
                                                Created
                                            </label>
                                            <input
                                                id="createdFrom"
                                                className="form-control"
                                                style={{ width: '150px', height: '40px' }}
                                                type="date"
                                                value={createdFrom}
                                                onChange={(e) => {
                                                    setCreatedFrom(e.target.value);
                                                    resetToFirstPage();
                                                }}
                                            />
                                            <span>–</span>
                                            <input
                                                aria-label="Created to"
                                                className="form-control"
                                                style={{ width: '150px', height: '40px' }}
                                                type="date"
                                                value={createdTo}
                                                onChange={(e) => {
                                                    setCreatedTo(e.target.value);
                                                    resetToFirstPage();
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <TableWrap style={{ overflowX: 'auto' }}>
                                        <Table>
                                            <MatTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                            <TableBody>
                                                {filteredUsers.sort(getSorting(order, orderBy)).map((d) => (
                                                    <TableRow role="checkbox" onClick={() => handleClick(d.id)} tabIndex={-1} key={d.id}>
                                                        <TableCell component="th" scope="row" padding="none">
                                                            {d.id}
                                                        </TableCell>
                                                        <TableCell>{d.email}</TableCell>
                                                        <TableCell>{getUserDisplayName(d)}</TableCell>
                                                        <TableCell className="text-capitalize">{d.crmType || '\u2014'}</TableCell>
                                                        <TableCell>{d.cohort || '\u2014'}</TableCell>
                                                        <TableCell>{orderStatusLabel(d.latestOrderStatus)}</TableCell>
                                                        <TableCell>{d.telegramGroupJoined ? 'Yes' : '\u2014'}</TableCell>
                                                        <TableCell>{d.role}</TableCell>
                                                        <TableCell>{d.subscriptionPlan}</TableCell>
                                                        <TableCell>{d.onBoarded ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{d.active ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{d.personaOnboarded ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{d.signupSource || '\u2014'}</TableCell>
                                                        <TableCell>{d.referredByCode || '\u2014'}</TableCell>
                                                        <TableCell>{formatDate(d.created_at)}</TableCell>
                                                        <TableCell>{formatDate(d.updated_at)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {filteredUsers.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={16} className="text-center">
                                                            No Data to display
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={usersResponse?.data?.total || 0}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{
                                            inputProps: { 'aria-label': 'rows per page' },
                                            native: true,
                                        }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Users;
